import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ClipboardList } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AdminListPage,
  AdminListTable,
  contentColumns,
  DescriptionField,
  ROW_LINK_CLASS,
  TitleSlugFields,
  useAdminListCreateState,
} from "~/components/cih/admin-list-page";
import { Badge } from "~/components/ui/badge";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { cn } from "~/lib/cn";
import {
  createFrameworkFn,
  frameworkQueryKeys,
  frameworksQueryOptions,
  type FrameworkListRow,
} from "~/lib/functions/cih/frameworks";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import {
  FRAMEWORK_TYPE_OPTIONS,
  type FrameworkType,
  frameworkTypeSchema,
} from "~/lib/shared/content-status";
import { nextSortSearch } from "~/lib/shared/sort";

const FRAMEWORK_SORTS = ["name", "created"] as const;
type FrameworkSort = (typeof FRAMEWORK_SORTS)[number];

const FRAMEWORK_SORT_DEFAULTS = listSortDefaults<FrameworkSort>("name", [
  "created",
]);

const FRAMEWORK_SORT_COLUMNS: Record<
  FrameworkSort,
  SortColumn<FrameworkListRow>
> = {
  name: { kind: "text", value: (row) => row.name },
  created: { kind: "date", value: (row) => row.createdAt },
};

export const Route = createFileRoute("/admin/cih/frameworks/")({
  staticData: { title: "Frameworks" },
  validateSearch: listSearchSchema(FRAMEWORK_SORTS),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(frameworksQueryOptions());
    return {};
  },
  component: FrameworksPage,
});

const FRAMEWORK_TYPE_COLORS: Record<FrameworkType, string> = {
  framework: "bg-blue-100 text-blue-700 border-blue-200",
  methodology: "bg-purple-100 text-purple-700 border-purple-200",
  standard: "bg-teal-100 text-teal-700 border-teal-200",
  protocol: "bg-orange-100 text-orange-700 border-orange-200",
};

function TypeBadge({ type }: { type: string }) {
  const parsed = frameworkTypeSchema.safeParse(type);
  const colorClass = parsed.success
    ? FRAMEWORK_TYPE_COLORS[parsed.data]
    : "bg-gray-100 text-gray-700";
  return (
    <Badge size="sm" className={cn("capitalize", colorClass)}>
      {type}
    </Badge>
  );
}

interface CreateFormState {
  name: string;
  slug: string;
  type: FrameworkType;
  description: string;
}

const EMPTY_FORM: CreateFormState = {
  name: "",
  slug: "",
  type: "framework",
  description: "",
};

const FRAMEWORK_COLUMNS: ColumnDef<FrameworkListRow>[] = [
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => (
      <Link
        to="/admin/cih/frameworks/$frameworkId"
        params={{ frameworkId: row.original.id }}
        className={ROW_LINK_CLASS}
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    id: "type",
    header: "Type",
    cell: ({ row }) => <TypeBadge type={row.original.type} />,
  },
  ...contentColumns<FrameworkListRow>({
    publicPath: (slug) => `/frameworks/${slug}`,
    pendingDraft: (row, className) =>
      row.pendingDraftId && (
        <Link
          to="/admin/cih/frameworks/$frameworkId"
          params={{ frameworkId: row.pendingDraftId }}
          className={className}
        >
          Draft pending
        </Link>
      ),
  }),
];

function FrameworksPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const urlSearch = Route.useSearch();
  const sort = urlSearch.sort ?? FRAMEWORK_SORT_DEFAULTS.sort;
  const direction = urlSearch.dir ?? FRAMEWORK_SORT_DEFAULTS.direction(sort);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const { create, form, setFormField } = useAdminListCreateState(EMPTY_FORM);

  const { data: frameworks, isLoading } = useQuery(
    frameworksQueryOptions(statusFilter || undefined),
  );

  const createMutation = useMutation({
    mutationFn: createFrameworkFn,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: frameworkQueryKeys.all });
      create.finish("Framework created.");
      navigate({
        to: "/admin/cih/frameworks/$frameworkId",
        params: { frameworkId: created.id },
      });
    },
  });

  const handleSubmit = (): void => {
    if (!form.name.trim() || !form.description.trim()) return;
    createMutation.mutate({
      data: {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        type: form.type,
        description: form.description.trim(),
      },
    });
  };

  const shownFrameworks = useMemo((): FrameworkListRow[] => {
    if (!frameworks) return [];
    const q = search.toLowerCase();
    const matches = q
      ? frameworks.filter(
          (f) =>
            f.name.toLowerCase().includes(q) ||
            (f.slug ?? "").toLowerCase().includes(q),
        )
      : frameworks;
    return sortListRows(
      matches,
      sort,
      direction,
      FRAMEWORK_SORT_COLUMNS,
      (f) => f.name,
    );
  }, [frameworks, search, sort, direction]);

  const changeSort = (column: FrameworkSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch({ sort, direction }, column, FRAMEWORK_SORT_DEFAULTS),
      }),
    });
  };

  const createFields = (
    <>
      <TitleSlugFields
        create={create}
        idPrefix="fw"
        titleLabel="Name"
        title={form.name}
        slug={form.slug}
        titlePlaceholder="e.g., GROW Model"
        slugPlaceholder="e.g., grow-model"
        onTitleChange={(val) => setFormField("name", val)}
        onSlugChange={(val) => setFormField("slug", val)}
      />
      <div>
        <Label htmlFor="fw-type" className="mb-1">
          Type
        </Label>
        <Select
          value={form.type}
          onValueChange={(val) => setFormField("type", val as FrameworkType)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FRAMEWORK_TYPE_OPTIONS.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DescriptionField
        id="fw-description"
        value={form.description}
        placeholder="Brief description of this framework..."
        onChange={(val) => setFormField("description", val)}
      />
    </>
  );

  return (
    <AdminListPage
      title="Frameworks"
      description="V1 methodology and framework pages"
      itemName="Framework"
      create={create}
      createFields={createFields}
      onCreateSubmit={handleSubmit}
      canCreate={Boolean(form.name.trim() && form.description.trim())}
      isCreating={createMutation.isPending}
      createError={createMutation.error}
      statusFilter={{ value: statusFilter, onChange: setStatusFilter }}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search frameworks...",
      }}
      isLoading={isLoading}
    >
      <AdminListTable
        rows={shownFrameworks}
        columns={FRAMEWORK_COLUMNS}
        sorts={FRAMEWORK_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        empty={{
          isFiltered: Boolean(search),
          icon: ClipboardList,
          itemName: "Framework",
          title: "No frameworks yet",
          description: "Create a methodology or framework page to get started.",
          onCreate: create.open,
        }}
      />
    </AdminListPage>
  );
}
